/**
 * Two-level rejection, ONE client derivation — mirrors the server's
 * `rejection.where.ts`: an entity (lead or deal) is REJECTED when its STATUS
 * carries `isRejection` OR its SUB-STATUS does. A rejected entity needs no
 * follow-up (canonical rule 5 — the server gates it out of every bucket and
 * count), so drawer surfaces use this to hide follow-up affordances on it
 * (user directive 2026-09-01: "for rejection status lead/deal, don't show
 * the follow-up there under any circumstances").
 *
 * Payload flags win when present; otherwise the PIPELINE config
 * (authoritative — detail payloads don't always include the flag) resolves
 * by status/sub-status id. Same logic StageSection / StatusAndUsersSection
 * carry inline; new consumers should use this resolver instead of a fourth
 * copy.
 */
export function buildEntityRejectionResolver(
  pipeline: any[] | null | undefined,
): (entity: any) => boolean {
  const byStatusId = new Map<number, boolean>();
  const bySubStatusId = new Map<number, boolean>();
  for (const stg of (pipeline ?? []) as any[]) {
    const statuses = stg?.statuses || stg?.statusList || [];
    for (const st of statuses) {
      byStatusId.set(Number(st.id), !!st.isRejection);
      for (const ss of st?.subStatuses || []) {
        bySubStatusId.set(Number(ss.id), !!ss.isRejection);
      }
    }
  }
  return (entity: any): boolean => {
    const subStatusId = entity?.subStatus?.id ?? entity?.subStatusId;
    if (
      (typeof entity?.subStatus?.isRejection === "boolean" &&
        entity.subStatus.isRejection) ||
      (subStatusId != null &&
        bySubStatusId.get(Number(subStatusId)) === true)
    ) {
      return true;
    }
    if (typeof entity?.status?.isRejection === "boolean") {
      return entity.status.isRejection;
    }
    const statusId = entity?.status?.id ?? entity?.statusId;
    if (statusId == null) return false;
    return byStatusId.get(Number(statusId)) === true;
  };
}
